import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import LegendCard from "@/components/feature/LegendCard";
import { legendStories } from "@/mocks/stories";

const verdictMap: Record<string, string> = {
  "有据可考": "这则校园怪谈经求证可追溯到明确的人、事与机制，真相清楚。",
  "有历史原型": "传说基于真实的历史旧事或实物，但细节在流传中被想象放大。",
  "证据不足存疑": "缺少可核实的直接证据，暂无法断定，我们诚实地保留存疑。",
  "基本可证为误传": "流传广泛的传说缺乏事实支撑，更可能是共享的叙事模板。",
};

export default function StoryDetailPage() {
  const { id } = useParams();
  const story = legendStories.find((s) => s.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    if (story) document.title = `${story.title} · 校园怪谈求证 · 校园爆料`;
  }, [story]);

  if (!story) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center bg-night-900 px-4">
        <div className="text-center">
          <i className="ri-file-unknow-line text-4xl text-parchment-300/40"></i>
          <h1 className="serif-head mt-4 text-2xl font-bold text-parchment-100">档案不存在或已被撤下</h1>
          <Link
            to="/archive"
            className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-cinnabar-600 px-6 py-3 text-parchment-100 transition hover:bg-cinnabar-500"
          >
            返回档案库
          </Link>
        </div>
      </section>
    );
  }

  const related = legendStories.filter((s) => s.id !== story.id).slice(0, 3);

  return (
    <>
      <section className="relative flex items-end overflow-hidden bg-night-900 pb-8 pt-20">
        <div className="absolute inset-0">
          <img src={story.cover} alt={story.title} title={story.title} className="h-full w-full object-cover object-center opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-night-900 via-night-900/60 to-night-900/30"></div>
        </div>
        <div className="relative z-10 mx-auto w-full max-w-4xl px-4 md:px-6">
          <nav className="mb-6 text-sm text-parchment-300/70">
            <Link to="/" className="hover:text-parchment-100">首页</Link>
            <i className="ri-arrow-right-s-line mx-1 align-middle"></i>
            <Link to="/archive" className="hover:text-parchment-100">怪谈档案库</Link>
            <i className="ri-arrow-right-s-line mx-1 align-middle"></i>
            <span className="text-parchment-200">{story.title}</span>
          </nav>
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-amberlight-400/40 bg-amberlight-400/10 px-3 py-1 text-sm text-amberlight-300">
            <i className="ri-folder-3-line"></i>
            {story.category}
          </span>
          <h1 className="serif-head mt-4 text-3xl font-black leading-snug text-parchment-100 md:text-5xl">
            {story.title}
          </h1>
          <p className="serif-head mt-3 text-lg text-parchment-300/80">{story.tagline}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-parchment-300/70">
            <span className="flex items-center gap-1.5"><i className="ri-map-pin-line text-amberlight-400"></i>{story.place}</span>
            <span className="flex items-center gap-1.5"><i className="ri-time-line text-amberlight-400"></i>{story.time}</span>
            <span className="flex items-center gap-1.5"><i className="ri-timer-line text-amberlight-400"></i>阅读约 {story.readTime}</span>
          </div>
        </div>
      </section>

      <article className="bg-night-900 py-12">
        <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 md:px-6 lg:grid-cols-[1fr_300px]">
          <div className="max-w-3xl">
            <div className="rounded-lg border-l-4 border-amberlight-400 bg-night-850 px-6 py-5">
              <p className="text-sm font-semibold uppercase tracking-wider text-amberlight-400">求证摘要</p>
              <p className="serif-head mt-2 text-lg leading-relaxed text-parchment-100">{story.excerpt}</p>
            </div>

            <div className="mt-8 space-y-6">
              {story.blocks.map((b, i) => {
                if (b.type === "h2") {
                  return (
                    <h2 key={i} className="serif-head pt-2 text-2xl font-bold text-parchment-100">
                      {b.text}
                    </h2>
                  );
                }
                if (b.type === "quote") {
                  return (
                    <blockquote
                      key={i}
                      className="relative rounded-lg border border-cinnabar-500/30 bg-cinnabar-600/5 px-7 py-5 text-parchment-200"
                    >
                      <i className="ri-double-quotes-l absolute -top-2 left-3 text-4xl text-cinnabar-500/40"></i>
                      <p className="serif-head text-base italic leading-relaxed">{b.text}</p>
                    </blockquote>
                  );
                }
                return (
                  <p key={i} className="text-base leading-loose text-parchment-300/90">
                    {b.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-12 rounded-lg border border-amberlight-400/30 bg-gradient-to-br from-night-850 to-night-800 p-7">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-2xl text-cinnabar-400"></i>
                <h2 className="serif-head text-xl font-bold text-parchment-100">求证结论</h2>
              </div>
              <span className="mt-3 inline-block rounded-full border border-amberlight-400/50 bg-amberlight-400/10 px-4 py-1.5 text-sm text-amberlight-300">
                结论：{story.verdict}
              </span>
              <p className="mt-4 text-base leading-loose text-parchment-300/90">{story.conclusion}</p>
              <p className="mt-4 border-t border-night-600/60 pt-4 text-xs leading-relaxed text-parchment-300/60">
                {verdictMap[story.verdict]} · 本站求证仅代表当前公开材料与走访结果，若你有补充线索，欢迎投稿更新档案。
              </p>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-night-600/50 bg-night-850 p-5">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-amberlight-400">求证原则</h4>
              <ul className="mt-4 space-y-3 text-sm text-parchment-300/80">
                <li className="flex gap-2"><i className="ri-arrow-right-s-line text-cinnabar-400"></i>不虚构当事人与结局</li>
                <li className="flex gap-2"><i className="ri-arrow-right-s-line text-cinnabar-400"></i>人物一律化名处理</li>
                <li className="flex gap-2"><i className="ri-arrow-right-s-line text-cinnabar-400"></i>没有证据不下结论</li>
                <li className="flex gap-2"><i className="ri-arrow-right-s-line text-cinnabar-400"></i>尊重集体记忆本身</li>
              </ul>
              <a
                href="mailto:editor@xybaoliao.example.com"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-cinnabar-500/60 bg-cinnabar-600/15 px-4 py-2.5 text-sm text-parchment-100 transition hover:bg-cinnabar-600"
              >
                补充线索 / 投稿
              </a>
            </div>

            <Link
              to="/archive"
              className="flex items-center justify-between rounded-lg border border-night-600/50 bg-night-850 px-5 py-4 text-parchment-200 transition hover:border-amberlight-400/50"
            >
              <span className="text-sm">返回档案库继续翻阅</span>
              <i className="ri-arrow-right-line text-amberlight-400"></i>
            </Link>
          </aside>
        </div>
      </article>

      <section className="border-t border-night-700/40 bg-[#191410] py-14">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <div className="mb-8 flex items-center justify-between">
            <h2 className="serif-head text-2xl font-bold text-parchment-100">更多待你求证的档案</h2>
            <Link to="/archive" className="flex items-center gap-1 whitespace-nowrap text-sm text-amberlight-400 hover:text-amberlight-300">
              全部档案 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((s) => (
              <LegendCard key={s.id} story={s} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}